import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  Stack,
  Divider,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Unstable_Grid2 as Grid,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  IconButton,
  Alert,
  Modal as MuiModal,
  CardContent,
  CardHeader,
  Backdrop,
  Box,
  CircularProgress,
  TextField,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import dayjs from "dayjs";

import SendIcon from "@mui/icons-material/Send";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { v4 as uuidv4 } from "uuid";
import axios from "axios";

import useAuthStore from "../../../../stores/auth";
import useNavStore from "../../../../stores/nav";
import { fetchData } from "../../../../fetchData";
import { humanFileSize } from "../../../../utils";
import api from "../../../../services/api";

import CompetenceSelector from "./components/CompetenceSelector";
import ERPSelector from "./components/ERPSelector";
import MonthTabs from "./components/MonthTabs";
import FileUploadTable, { StagedFileView } from "./components/FileUploadTable";
import FeatureFeedback from "../../../../components/FeatureFeedback";

const STORAGE_KEY = `${process.env.REACT_APP_KEYNAME || ""}ContabilPeriodDraft`;

const steps = ["Período", "ERP", "Anexos", "Revisão"];

type FileTipo = "extrato" | "relatorioFinanc" | "docs";

interface StagedFile {
  id: string;
  file: File;
  name: string;
  originalName: string;
  sizeBytes: number;
  tipo: FileTipo;
  month: string | null;
  pendingMode: boolean;
  status: "idle" | "uploading" | "uploaded" | "error";
  progress: number;
  error?: string;
  key?: string;
  fileUrl?: string;
}

const UPLOAD_CONCURRENCY = 3;

const erpOptions = [
  "Atos 6",
  "Bling",
  "Church",
  "Conta Azul",
  "Eklesia",
  "IAS Finance",
  "Omie",
  "Prover",
  "Outros",
];

const NewPeriod = () => {
  const navigate = useNavigate();
  const { draftId } = useParams<{ draftId?: string }>();
  const company = useAuthStore((state) => state.company);
  const user = useAuthStore((state) => state.user);
  const setHeaderTitle = useNavStore((state: any) => state.setHeader);
  const setToolbar = useNavStore((state: any) => state.setToolbar);
  const [activeStep, setActiveStep] = useState(0);
  const [pendingLoading, setPendingLoading] = useState(!!draftId);
  const [competenceType, setCompetenceType] = useState<"single" | "range">(
    "single",
  );
  const [singleMonth, setSingleMonth] = useState<string>("");
  const [rangeStart, setRangeStart] = useState<string>("");
  const [rangeEnd, setRangeEnd] = useState<string>("");
  const [hasErp, setHasErp] = useState<"yes" | "no" | "">("");
  const [selectedErp, setSelectedErp] = useState("");
  const [otherErp, setOtherErp] = useState("");
  const [extractFiles, setExtractFiles] = useState<StagedFile[]>([]);
  const [financialReportFiles, setFinancialReportFiles] = useState<
    StagedFile[]
  >([]);
  const [documentsFiles, setDocumentsFiles] = useState<StagedFile[]>([]);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [selectedMonthTab, setSelectedMonthTab] = useState(0);
  const [erpNoDialogOpen, setErpNoDialogOpen] = useState(false);
  const [allFilesCount, setAllFilesCount] = useState({
    extrato: 0,
    relatorioFinanc: 0,
    docs: 0,
  });
  const [errors, setErrors] = useState<{
    erp?: string;
    extract?: string;
    financialReport?: string;
  }>({});
  const [occupiedPeriods, setOccupiedPeriods] = useState<Set<string>>(
    new Set(),
  );
  const [taskComment, setTaskComment] = useState("");
  const [replyComment, setReplyComment] = useState("");
  const [showPendenciasModal, setShowPendenciasModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitProgressLabel, setSubmitProgressLabel] = useState("");
  const [failedFilesDialog, setFailedFilesDialog] = useState<{
    open: boolean;
    files: StagedFile[];
  }>({ open: false, files: [] });
  const failedFilesResolverRef = useRef<
    ((action: "retry" | "skip" | "cancel") => void) | null
  >(null);
  const snackbar = useNavStore((state: any) => state.setSnackbar);

  const guardUploading = () => {
    if (!isSubmitting) return false;
    snackbar("Aguarde a conclusão do envio antes de continuar.", "warning");
    return true;
  };

  const getDraftData = useCallback(() => {
    return {
      competenceType,
      singleMonth,
      rangeStart,
      rangeEnd,
      hasErp,
      selectedErp,
      otherErp,
      selectedMonthTab,
    };
  }, [
    competenceType,
    singleMonth,
    rangeStart,
    rangeEnd,
    hasErp,
    selectedErp,
    otherErp,
    selectedMonthTab,
  ]);

  const saveDraftToLocalStorage = useCallback(() => {
    const draftData = getDraftData();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(draftData));
  }, [getDraftData]);

  const loadOccupiedPeriods = useCallback(async () => {
    if (!company?._id) return new Set<string>();
    try {
      const response = await fetchData(
        `/accounting/drafts?limit=1000&company=${company._id}`,
        {
          method: "GET",
        },
      );
      if (response?.rows) {
        const periods = new Set<string>(
          response.rows
            .map((row: any) => row.period)
            .filter((period: any): period is string => Boolean(period)),
        );
        setOccupiedPeriods(periods);
        return periods;
      }
      return new Set<string>();
    } catch (error) {
      console.error("Erro ao carregar períodos ocupados:", error);
      return new Set<string>();
    }
  }, [company]);

  const findFirstAvailableMonth = useCallback((occupied: Set<string>) => {
    let current = dayjs().subtract(1, "month");
    const maxAttempts = 24;
    let attempts = 0;

    while (attempts < maxAttempts) {
      const periodKey = current.format("YYYY-MM");
      if (!occupied.has(periodKey)) {
        return current.format("MM/YYYY");
      }
      current = current.subtract(1, "month");
      attempts++;
    }
    return dayjs().subtract(1, "month").format("MM/YYYY");
  }, []);

  const findFirstAvailableRange = useCallback((occupied: Set<string>) => {
    let endDate = dayjs().subtract(1, "month");
    const maxAttempts = 24;
    let attempts = 0;
    let availableEnd: string | null = null;

    while (attempts < maxAttempts && !availableEnd) {
      const periodKey = endDate.format("YYYY-MM");
      if (!occupied.has(periodKey)) {
        availableEnd = endDate.format("MM/YYYY");
      } else {
        endDate = endDate.subtract(1, "month");
        attempts++;
      }
    }

    if (!availableEnd) {
      availableEnd = dayjs().subtract(1, "month").format("MM/YYYY");
    }

    const [endMonthNum, endYear] = availableEnd.split("/");
    let startDate = dayjs(`${endYear}-${endMonthNum}-01`).subtract(1, "month");
    attempts = 0;
    let availableStart: string | null = null;

    while (attempts < maxAttempts && !availableStart) {
      const periodKey = startDate.format("YYYY-MM");
      if (!occupied.has(periodKey)) {
        availableStart = startDate.format("MM/YYYY");
      } else {
        startDate = startDate.subtract(1, "month");
        attempts++;
      }
    }

    if (!availableStart) {
      availableStart = dayjs(`${endYear}-${endMonthNum}-01`)
        .subtract(1, "month")
        .format("MM/YYYY");
    }

    return {
      start: availableStart,
      end: availableEnd,
    };
  }, []);

  const loadDraftFromLocalStorage = useCallback(
    (occupied: Set<string>) => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          const draftData = JSON.parse(saved);
          setCompetenceType(draftData.competenceType || "single");
          const availableMonth = findFirstAvailableMonth(occupied);
          const availableRange = findFirstAvailableRange(occupied);

          if (draftData.competenceType === "range") {
            setSingleMonth(draftData.singleMonth || availableMonth);
            setRangeStart(draftData.rangeStart || availableRange.start);
            setRangeEnd(draftData.rangeEnd || availableRange.end);
          } else {
            setSingleMonth(draftData.singleMonth || availableMonth);
            setRangeStart(draftData.rangeStart || availableRange.start);
            setRangeEnd(draftData.rangeEnd || availableRange.end);
          }
          setHasErp(draftData.hasErp || "");
          setSelectedErp(draftData.selectedErp || "");
          setOtherErp(draftData.otherErp || "");
          setSelectedMonthTab(draftData.selectedMonthTab || 0);
        } else {
          const availableMonth = findFirstAvailableMonth(occupied);
          const availableRange = findFirstAvailableRange(occupied);
          setSingleMonth(availableMonth);
          setRangeStart(availableRange.start);
          setRangeEnd(availableRange.end);
        }
      } catch (error) {
        console.error("Erro ao carregar rascunho:", error);
        const availableMonth = findFirstAvailableMonth(occupied);
        const availableRange = findFirstAvailableRange(occupied);
        setSingleMonth(availableMonth);
        setRangeStart(availableRange.start);
        setRangeEnd(availableRange.end);
      }
    },
    [findFirstAvailableMonth, findFirstAvailableRange],
  );

  const saveDraftToBackend = useCallback(async () => {
    saveDraftToLocalStorage();
  }, [saveDraftToLocalStorage]);

  const validateFileExtension = (
    fileName: string,
    tipo: "extrato" | "relatorioFinanc" | "docs",
    pendingMode = false,
  ): boolean => {
    const extension = fileName.split(".").pop()?.toLowerCase() || "";

    if (tipo === "extrato") {
      const allowed = ["xlsx", "xls", "ofx", "csv", "pdf"];
      return allowed.includes(extension);
    }

    if (tipo === "relatorioFinanc") {
      const allowed = ["xlsx", "csv", "txt"];
      return allowed.includes(extension);
    }

    if (tipo === "docs") {
      const allowed = pendingMode
        ? ["pdf", "txt", "jpg", "jpeg", "png", "xls", "xlsx", "ofx", "csv"]
        : ["pdf", "txt", "jpg", "jpeg", "png"];
      return allowed.includes(extension);
    }

    return true;
  };

  const parseCompetence = (value: string) => {
    const [month, year] = value.split("/");
    return { month, year: parseInt(year) };
  };

  const getCurrentMonthPath = () => {
    if (competenceType === "single") {
      const { month, year } = parseCompetence(singleMonth);
      return { month, year };
    } else {
      const start = parseCompetence(rangeStart);
      const end = parseCompetence(rangeEnd);
      const startDate = dayjs(`${start.year}-${start.month}-01`);
      const endDate = dayjs(`${end.year}-${end.month}-01`);
      const monthTabs = [];
      let current = startDate;
      while (current.isBefore(endDate) || current.isSame(endDate, "month")) {
        monthTabs.push(current.format("MM/YYYY"));
        current = current.add(1, "month");
      }
      if (monthTabs.length > 0 && selectedMonthTab < monthTabs.length) {
        const selectedMonthStr = monthTabs[selectedMonthTab];
        const { month, year } = parseCompetence(selectedMonthStr);
        return { month, year };
      }
      return null;
    }
  };

  const buildAccountingPath = (period: string) => {
    const [yyyy, mm] = period.split("-");
    return `accounting/${yyyy}/${parseInt(mm || "0", 10)}`;
  };

  const getFileNameTransform = (
    tipo: "extrato" | "relatorioFinanc" | "docs",
  ) => {
    const prefix =
      tipo === "extrato"
        ? "EXTRATOS-"
        : tipo === "relatorioFinanc"
        ? "FINANCEIRO-"
        : "DOCUMENTOS-";
    return (fileName: string) => `${prefix}${fileName}`;
  };

  const getPendingFileNameTransform = () => {
    return (fileName: string) => `PENDENCIA-${fileName}`;
  };

  const getDragAndDropKey = (tipo: string) => {
    const currentMonth = getCurrentMonthPath();
    const monthKey = currentMonth
      ? `${currentMonth.year}-${currentMonth.month}`
      : selectedMonthTab;
    return `${tipo}-${monthKey}`;
  };

  const getFilesForCurrentMonth = (
    files: StagedFile[],
    tipo: FileTipo,
  ): StagedFileView[] => {
    const currentMonth = getCurrentMonthPath();
    const monthStr = currentMonth
      ? `${currentMonth.year}-${currentMonth.month}`
      : null;

    if (!monthStr) return [];

    return files
      .filter((f) => f.month === monthStr && f.tipo === tipo)
      .map((file) => ({
        id: file.id,
        name: file.name,
        size: humanFileSize(file.sizeBytes || 0),
        status: file.status,
        progress: file.progress,
        error: file.error,
      }));
  };

  const MAX_FILE_SIZE = 50 * 1024 * 1024;

  const handleFilesSelected = (
    selectedFiles: File[],
    tipo: FileTipo,
    pendingMode = false,
  ) => {
    const currentMonth = getCurrentMonthPath();
    const monthStr = currentMonth
      ? `${currentMonth.year}-${currentMonth.month}`
      : null;

    const nameTransform = pendingMode
      ? getPendingFileNameTransform()
      : getFileNameTransform(tipo);

    const validFiles: StagedFile[] = [];

    for (const file of selectedFiles) {
      if (!validateFileExtension(file.name, tipo, pendingMode)) {
        const tipoNome =
          tipo === "relatorioFinanc"
            ? "relatório financeiro"
            : tipo === "docs"
            ? "documentos"
            : "extrato";
        const allowedExts =
          tipo === "extrato"
            ? "XLSX, XLS, OFX, CSV e PDF"
            : tipo === "relatorioFinanc"
            ? "XLSX, CSV e TXT"
            : pendingMode
            ? "PDF, TXT, JPG, PNG, XLS, XLSX, OFX e CSV"
            : "PDF, TXT, JPG e PNG";
        snackbar(
          `Extensão não permitida para ${tipoNome}. Extensões permitidas: ${allowedExts}`,
          "error",
        );
        continue;
      }

      if (file.size > MAX_FILE_SIZE) {
        snackbar(
          `Arquivo "${file.name}" excede o limite de 50MB. Tamanho: ${(
            file.size /
            1024 /
            1024
          ).toFixed(2)}MB`,
          "error",
        );
        continue;
      }

      const normalizedName = file.name
        .normalize("NFD")
        .replace(/[^a-zA-Z0-9.\s]/g, "");

      validFiles.push({
        id: uuidv4(),
        file,
        name: nameTransform(normalizedName),
        originalName: file.name,
        sizeBytes: file.size,
        tipo,
        month: monthStr,
        pendingMode,
        status: "idle",
        progress: 0,
      });
    }

    if (validFiles.length === 0) return;

    if (tipo === "extrato") {
      setExtractFiles((prev) => [...prev, ...validFiles]);
    } else if (tipo === "relatorioFinanc") {
      setFinancialReportFiles((prev) => [...prev, ...validFiles]);
    } else {
      setDocumentsFiles((prev) => [...prev, ...validFiles]);
    }
  };

  const handleRemoveStagedFile = (id: string, tipo: FileTipo) => {
    if (tipo === "extrato") {
      setExtractFiles((prev) => prev.filter((f) => f.id !== id));
    } else if (tipo === "relatorioFinanc") {
      setFinancialReportFiles((prev) => prev.filter((f) => f.id !== id));
    } else {
      setDocumentsFiles((prev) => prev.filter((f) => f.id !== id));
    }
  };

  const handleSair = () => {
    if (guardUploading()) return;
    navigate("/contabil");
  };

  const validateAllMonths = (): {
    allMonthsHaveExtract: boolean;
    missingMonths: string[];
    allMonthsHaveFinancialReport?: boolean;
    missingFinancialReportMonths?: string[];
  } => {
    if (competenceType === "single") {
      const hasExtract = extractFiles.length > 0;
      const hasFinancialReport = financialReportFiles.length > 0;

      return {
        allMonthsHaveExtract: hasExtract,
        missingMonths: hasExtract ? [] : [singleMonth],
        allMonthsHaveFinancialReport: hasFinancialReport,
        missingFinancialReportMonths: hasFinancialReport ? [] : [singleMonth],
      };
    } else {
      const start = parseCompetence(rangeStart);
      const end = parseCompetence(rangeEnd);
      const startDate = dayjs(`${start.year}-${start.month}-01`);
      const endDate = dayjs(`${end.year}-${end.month}-01`);
      const months = [];
      let current = startDate;
      while (current.isBefore(endDate) || current.isSame(endDate, "month")) {
        months.push(current.format("MM/YYYY"));
        current = current.add(1, "month");
      }

      const monthsSet = new Set(
        months.map((m) => {
          const [month, year] = m.split("/");
          return `${year}-${month}`;
        }),
      );

      const extractMonths = new Set(
        extractFiles.map((f) => f.month).filter(Boolean),
      );
      const financialReportMonths = new Set(
        financialReportFiles.map((f) => f.month).filter(Boolean),
      );

      const missingExtractMonths = Array.from(monthsSet).filter(
        (m) => !extractMonths.has(m),
      );
      const missingFinancialReportMonths =
        hasErp === "no"
          ? Array.from(monthsSet).filter((m) => !financialReportMonths.has(m))
          : [];

      return {
        allMonthsHaveExtract: missingExtractMonths.length === 0,
        missingMonths: missingExtractMonths.map((m) => {
          const [year, month] = m.split("-");
          return `${month}/${year}`;
        }),
        allMonthsHaveFinancialReport:
          hasErp === "no"
            ? missingFinancialReportMonths.length === 0
            : undefined,
        missingFinancialReportMonths:
          hasErp === "no"
            ? missingFinancialReportMonths.map((m) => {
                const [year, month] = m.split("-");
                return `${month}/${year}`;
              })
            : undefined,
      };
    }
  };

  const validateForm = () => {
    const newErrors: {
      erp?: string;
      extract?: string;
      financialReport?: string;
    } = {};

    if (competenceType === "range") {
      if (!rangeStart || !rangeEnd) {
        snackbar("Selecione o período inicial e final", "error");
        return false;
      }
      const start = parseCompetence(rangeStart);
      const end = parseCompetence(rangeEnd);
      const startDate = dayjs(`${start.year}-${start.month}-01`);
      const endDate = dayjs(`${end.year}-${end.month}-01`);

      if (startDate.isSame(endDate, "month")) {
        snackbar(
          "No modo período, é necessário selecionar pelo menos 2 meses diferentes. Use a opção 'Mensal' para um único mês.",
          "error",
        );
        return false;
      }
    }

    if (competenceType === "single" && !singleMonth) {
      snackbar("Selecione um mês", "error");
      return false;
    }

    if (!hasErp) {
      newErrors.erp = "Selecione se possui ERP integrado";
    } else if (hasErp === "yes" && !selectedErp) {
      newErrors.erp = "Selecione o ERP";
    } else if (hasErp === "yes" && selectedErp === "Outros" && !otherErp) {
      newErrors.erp = "Informe o nome do ERP";
    }

    const {
      allMonthsHaveExtract,
      missingMonths,
      allMonthsHaveFinancialReport,
      missingFinancialReportMonths,
    } = validateAllMonths();
    if (!allMonthsHaveExtract) {
      if (competenceType === "single") {
        newErrors.extract = "Envio de extrato é obrigatório";
      } else {
        newErrors.extract = `É necessário anexar extrato para todos os meses do período. Faltam: ${missingMonths.join(
          ", ",
        )}`;
      }
    }

    if (
      hasErp === "no" &&
      allMonthsHaveFinancialReport !== undefined &&
      !allMonthsHaveFinancialReport
    ) {
      if (competenceType === "single") {
        newErrors.financialReport =
          "Envio de relatório financeiro é obrigatório quando não possui ERP integrado";
      } else {
        newErrors.financialReport = `É necessário anexar relatório financeiro para todos os meses do período quando não possui ERP integrado. Faltam: ${missingFinancialReportMonths?.join(
          ", ",
        )}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendClick = () => {
    if (guardUploading()) return;
    if (draftId) {
      const hasFiles = documentsFiles.length > 0;
      const hasComment = replyComment.trim().length > 0;
      if (!hasFiles && !hasComment) {
        snackbar(
          "Envie pelo menos um anexo ou preencha a mensagem de resposta.",
          "error",
        );
        return;
      }
      setConfirmSendOpen(true);
      return;
    }
    const isValid = validateForm();
    if (isValid) {
      setConfirmSendOpen(true);
    }
  };

  const getAllFilesForPeriod = () => {
    return {
      extrato: extractFiles,
      relatorioFinanc: financialReportFiles,
      docs: documentsFiles,
    };
  };

  const toApiFile = (f: StagedFile) => ({
    key: f.key || "",
    fileName: f.name,
    fileUrl: f.fileUrl || "",
    size: f.sizeBytes,
  });

  const updateStagedFile = (
    tipo: FileTipo,
    id: string,
    patch: Partial<StagedFile>,
  ) => {
    const updater = (prev: StagedFile[]) =>
      prev.map((f) => (f.id === id ? { ...f, ...patch } : f));
    if (tipo === "extrato") setExtractFiles(updater);
    else if (tipo === "relatorioFinanc") setFinancialReportFiles(updater);
    else setDocumentsFiles(updater);
  };

  const removeStagedFiles = (filesToRemove: StagedFile[]) => {
    const idsByTipo: Record<FileTipo, Set<string>> = {
      extrato: new Set(),
      relatorioFinanc: new Set(),
      docs: new Set(),
    };
    filesToRemove.forEach((f) => idsByTipo[f.tipo].add(f.id));
    setExtractFiles((prev) => prev.filter((f) => !idsByTipo.extrato.has(f.id)));
    setFinancialReportFiles((prev) =>
      prev.filter((f) => !idsByTipo.relatorioFinanc.has(f.id)),
    );
    setDocumentsFiles((prev) => prev.filter((f) => !idsByTipo.docs.has(f.id)));
  };

  const uploadSingleStagedFile = async (
    stagedFile: StagedFile,
    companyId: string,
  ): Promise<StagedFile> => {
    updateStagedFile(stagedFile.tipo, stagedFile.id, {
      status: "uploading",
      progress: 0,
      error: undefined,
    });
    try {
      const path = stagedFile.month
        ? buildAccountingPath(stagedFile.month)
        : "";

      // 1. Solicitar presigned PUT URL
      let presignRes: any;
      try {
        presignRes = await api().post(`cloud/${companyId}/presign`, {
          fileName: stagedFile.name,
          contentType: stagedFile.file.type || "application/octet-stream",
          size: stagedFile.file.size,
          path,
          tipo: stagedFile.tipo,
          pendingMode: stagedFile.pendingMode ?? false,
        });
      } catch (err: any) {
        const status = err?.response?.status;
        const msg =
          status === 403
            ? "Sem permissão para enviar o arquivo"
            : status === 0 || err?.message?.toLowerCase().includes("cors")
            ? "Erro de CORS: verifique a configuração do bucket"
            : err?.response?.data?.message || "Erro ao preparar envio";
        updateStagedFile(stagedFile.tipo, stagedFile.id, {
          status: "error",
          error: msg,
        });
        return { ...stagedFile, status: "error", error: msg };
      }

      const { url: presignedUrl, key, fileUrl, headers } = presignRes.data;

      // 2. PUT direto no S3 (sem passar pelo backend)
      try {
        await axios.put(presignedUrl, stagedFile.file, {
          headers: {
            ...headers,
          },
          onUploadProgress: (e: ProgressEvent) => {
            const progress = Math.round((e.loaded * 100) / (e.total || 1));
            updateStagedFile(stagedFile.tipo, stagedFile.id, { progress });
          },
        });
      } catch (err: any) {
        const status = err?.response?.status;
        const msg =
          status === 403
            ? "Erro 403: bucket sem permissão ou CORS não configurado"
            : status === 0 || !status
            ? "Erro de CORS no upload direto ao S3"
            : err?.message?.includes("timeout")
            ? "Timeout no envio"
            : `Erro no envio do arquivo (${status || "rede"})`;
        updateStagedFile(stagedFile.tipo, stagedFile.id, {
          status: "error",
          error: msg,
        });
        return { ...stagedFile, status: "error", error: msg };
      }

      // 3. Confirmar upload
      try {
        await api().post(`cloud/${companyId}/presign/confirm`, {
          key,
          size: stagedFile.file.size,
        });
      } catch (err: any) {
        const msg = err?.message?.includes("timeout")
          ? "Timeout na confirmação do upload"
          : err?.response?.data?.message || "Erro na confirmação do upload";
        updateStagedFile(stagedFile.tipo, stagedFile.id, {
          status: "error",
          error: msg,
        });
        return { ...stagedFile, status: "error", error: msg };
      }

      // 4. Sucesso — atualizar estado
      updateStagedFile(stagedFile.tipo, stagedFile.id, {
        status: "uploaded",
        progress: 100,
        key,
        fileUrl,
      });
      return { ...stagedFile, status: "uploaded", progress: 100, key, fileUrl };
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Falha no envio";
      updateStagedFile(stagedFile.tipo, stagedFile.id, {
        status: "error",
        error: msg,
      });
      return { ...stagedFile, status: "error", error: msg };
    }
  };

  const uploadFilesWithConcurrency = async (
    filesToUpload: StagedFile[],
    companyId: string,
  ): Promise<{ succeeded: StagedFile[]; failed: StagedFile[] }> => {
    const queue = [...filesToUpload];
    const results: StagedFile[] = [];

    const worker = async () => {
      while (queue.length > 0) {
        const next = queue.shift();
        if (!next) return;
        const result = await uploadSingleStagedFile(next, companyId);
        results.push(result);
        setSubmitProgressLabel(
          `Enviando arquivos... ${results.length} de ${filesToUpload.length}`,
        );
      }
    };

    const workerCount = Math.min(UPLOAD_CONCURRENCY, filesToUpload.length);
    await Promise.all(Array.from({ length: workerCount }, worker));

    return {
      succeeded: results.filter((f) => f.status === "uploaded"),
      failed: results.filter((f) => f.status === "error"),
    };
  };

  const askAboutFailedFiles = (
    failed: StagedFile[],
  ): Promise<"retry" | "skip" | "cancel"> => {
    return new Promise((resolve) => {
      failedFilesResolverRef.current = resolve;
      setFailedFilesDialog({ open: true, files: failed });
    });
  };

  const resolveFailedFilesDialog = (action: "retry" | "skip" | "cancel") => {
    setFailedFilesDialog({ open: false, files: [] });
    if (failedFilesResolverRef.current) {
      failedFilesResolverRef.current(action);
      failedFilesResolverRef.current = null;
    }
  };

  const uploadAllPendingFiles = async (companyId: string): Promise<boolean> => {
    let pending = [
      ...extractFiles,
      ...financialReportFiles,
      ...documentsFiles,
    ].filter((f) => f.status !== "uploaded");

    while (pending.length > 0) {
      setSubmitProgressLabel(`Enviando arquivos... 0 de ${pending.length}`);
      const { failed } = await uploadFilesWithConcurrency(pending, companyId);
      if (failed.length === 0) return true;

      const action = await askAboutFailedFiles(failed);
      if (action === "cancel") return false;
      if (action === "skip") {
        removeStagedFiles(failed);
        snackbar(
          "Envio prosseguiu sem os arquivos com falha. Envie-os posteriormente ao suporte, se necessário.",
          "warning",
        );
        return true;
      }
      pending = failed;
    }
    return true;
  };

  const handleConfirmSend = async () => {
    if (guardUploading()) return;
    setConfirmSendOpen(false);

    if (!user || !company?._id) {
      snackbar("Erro: usuário ou empresa não autenticado", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const uploadOk = await uploadAllPendingFiles(company._id);
      if (!uploadOk) return;

      if (draftId) {
        const files = {
          extrato: [],
          relatorioFinanc: [],
          docs: documentsFiles
            .filter((f) => f.status === "uploaded")
            .map(toApiFile),
        };
        const comment = `Anexos contábeis:\n${window.location.origin}/contabil/pending/${draftId}`;
        await fetchData(`/accounting/drafts/${draftId}`, {
          method: "PATCH",
          body: JSON.stringify({
            files,
            status: "queued",
            comment,
            ...(replyComment.trim() && {
              userMessage: replyComment.trim(),
              userName: user?.name,
            }),
          }),
        });

        const [mm, yyyy] = (singleMonth || "").split("/");
        const period = yyyy && mm ? [`${yyyy}-${mm}`] : [];
        try {
          await fetchData("/histories", {
            method: "POST",
            body: JSON.stringify({
              user: (user as any)?._id || (user as any)?.sub,
              username: user.name,
              type: "organization",
              type_id: company._id,
              content: "Período contábil enviado para processamento",
              data: { period, files },
            }),
          });
        } catch (_) {
          console.error("Erro ao criar history:", _);
        }

        setReplyComment("");
        snackbar(
          "Anexos enviados. O período foi recolocado na fila.",
          "success",
        );
        navigate("/contabil");
        return;
      }

      const getCompetenceArray = () => {
        if (competenceType === "single") {
          const [month, year] = singleMonth.split("/");
          return [`${year}-${month}`];
        } else {
          const start = parseCompetence(rangeStart);
          const end = parseCompetence(rangeEnd);
          const startDate = dayjs(`${start.year}-${start.month}-01`);
          const endDate = dayjs(`${end.year}-${end.month}-01`);
          const competences = [];
          let current = startDate;
          while (
            current.isBefore(endDate) ||
            current.isSame(endDate, "month")
          ) {
            competences.push(current.format("YYYY-MM"));
            current = current.add(1, "month");
          }
          return competences;
        }
      };

      const periods = getCompetenceArray();

      if (competenceType === "range" && periods.length < 2) {
        snackbar(
          "No modo período, é necessário selecionar pelo menos 2 meses diferentes. Use a opção 'Mensal' para um único mês.",
          "error",
        );
        return;
      }

      const occupiedInSelection = periods.filter((period) =>
        occupiedPeriods.has(period),
      );
      if (occupiedInSelection.length > 0) {
        snackbar(
          "Um ou mais períodos selecionados já possuem registros. Por favor, selecione apenas períodos disponíveis.",
          "error",
        );
        return;
      }

      const erpValue =
        hasErp === "yes"
          ? selectedErp === "Outros"
            ? otherErp
            : selectedErp || "null"
          : "null";

      const uploadedFiles = getAllFilesForPeriod();
      const onlyUploaded = {
        extrato: uploadedFiles.extrato.filter((f) => f.status === "uploaded"),
        relatorioFinanc: uploadedFiles.relatorioFinanc.filter(
          (f) => f.status === "uploaded",
        ),
        docs: uploadedFiles.docs.filter((f) => f.status === "uploaded"),
      };

      const drafts = periods.map((period) => ({
        period,
        erp: erpValue,
        files: {
          extrato: onlyUploaded.extrato
            .filter((file) => file.month === period)
            .map(toApiFile),
          relatorioFinanc: onlyUploaded.relatorioFinanc
            .filter((file) => file.month === period)
            .map(toApiFile),
          docs: onlyUploaded.docs
            .filter((file) => file.month === period)
            .map(toApiFile),
        },
        user: user?.name || "",
        user_id: (user as any)?._id || (user as any)?.sub || "",
        company: company.reference,
      }));

      const accountingDrafts = await fetchData("/accounting/drafts", {
        method: "POST",
        body: JSON.stringify(drafts),
      });

      const draftIds = Array.isArray(accountingDrafts)
        ? accountingDrafts.map((draft: any) => draft._id)
        : [accountingDrafts._id];

      try {
        await fetchData("/histories", {
          method: "POST",
          body: JSON.stringify({
            user: (user as any)?._id || (user as any)?.sub,
            username: user.name,
            type: "organization",
            type_id: company._id,
            content: "Período contábil enviado para processamento",
            data: {
              period: periods,
              files: {
                extrato: onlyUploaded.extrato.map(toApiFile),
                relatorioFinanc: onlyUploaded.relatorioFinanc.map(toApiFile),
                docs: onlyUploaded.docs.map(toApiFile),
              },
              draftIds,
            },
          }),
        });
      } catch (_) {
        console.error("Erro ao criar history:", _);
      }

      snackbar("Período contábil enviado com sucesso!", "success");
      try {
        const feedbackState = await fetchData("/feedback/track", {
          method: "POST",
          body: JSON.stringify({ context: "ces_accounting_draft" }),
        });
        if (feedbackState?.shouldAsk) {
          setShowFeedbackModal(true);
          return;
        }
      } catch (e) {
        console.log(e);
      }
      navigate("/contabil");
    } catch (error) {
      snackbar(
        "Erro ao enviar período contábil, entre em contato com o suporte",
        "error",
      );
      console.error("Erro ao enviar período contábil:", error);
    } finally {
      setIsSubmitting(false);
      setSubmitProgressLabel("");
    }
  };

  useEffect(() => {
    setHeaderTitle(draftId ? "Complementar anexos" : "Enviar novo período");
    setToolbar(null);
    if (draftId) {
      const loadPending = async () => {
        try {
          const draft = await fetchData(`/accounting/drafts/${draftId}`, {
            method: "GET",
          });
          if (!draft) {
            snackbar("Período não encontrado", "error");
            navigate("/contabil");
            return;
          }
          if (draft.status !== "pending") {
            snackbar(
              "Este período não está pendente. Acesso permitido apenas para complementar anexos de períodos com pendências.",
              "warning",
            );
            navigate("/contabil");
            return;
          }
          const [yyyy, mm] = (draft.period || "").split("-");
          setSingleMonth(mm && yyyy ? `${mm}/${yyyy}` : "");
          setCompetenceType("single");
          const hasErpVal =
            draft.erp && draft.erp !== "null" && String(draft.erp).trim()
              ? "yes"
              : "no";
          setHasErp(hasErpVal);
          if (hasErpVal === "yes" && draft.erp) {
            const erp = String(draft.erp);
            setSelectedErp(erpOptions.includes(erp) ? erp : "Outros");
            setOtherErp(erpOptions.includes(erp) ? "" : erp);
          }
          setTaskComment(draft.taskComment || "");
          setActiveStep(2);
          if (draft.taskComment) {
            setShowPendenciasModal(true);
          }
        } catch (e) {
          snackbar("Erro ao carregar período", "error");
          navigate("/contabil");
        } finally {
          setPendingLoading(false);
        }
      };
      loadPending();
    } else {
      const initialize = async () => {
        const occupied = await loadOccupiedPeriods();
        loadDraftFromLocalStorage(occupied);
        if (!company?._id) return;
        try {
          const response = await fetchData(
            `/accounting/drafts?limit=1&company=${company._id}`,
            { method: "GET" },
          );
          const rows = response?.rows ?? [];
          if (rows.length === 0) return;
          const lastErp = rows[0].erp;
          if (
            lastErp == null ||
            lastErp === "null" ||
            String(lastErp).trim() === ""
          ) {
            setHasErp("no");
            setSelectedErp("");
            setOtherErp("");
          } else {
            const erpStr = String(lastErp).trim();
            const match = erpOptions.find(
              (opt) => opt.toLowerCase() === erpStr.toLowerCase(),
            );
            if (match) {
              setHasErp("yes");
              setSelectedErp(match);
              setOtherErp("");
            } else {
              setHasErp("yes");
              setSelectedErp("Outros");
              setOtherErp(erpStr);
            }
          }
        } catch {
          // ignore; leave ERP as loaded from localStorage or empty
        }
      };
      initialize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  useEffect(() => {
    if (draftId) return;
    saveDraftToLocalStorage();
    const debounceTimer = setTimeout(() => {
      saveDraftToBackend();
    }, 2000);
    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    draftId,
    competenceType,
    singleMonth,
    rangeStart,
    rangeEnd,
    hasErp,
    selectedErp,
    otherErp,
    selectedMonthTab,
  ]);

  useEffect(() => {
    if (activeStep === 3) {
      setAllFilesCount({
        extrato: extractFiles.length,
        relatorioFinanc: financialReportFiles.length,
        docs: documentsFiles.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, extractFiles, financialReportFiles, documentsFiles]);

  useEffect(() => {
    if (!isSubmitting) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isSubmitting]);

  const handleNext = () => {
    if (guardUploading()) return;
    if (activeStep === 0) {
      if (!competenceType) {
        snackbar("Selecione o tipo de período", "error");
        return;
      }
      if (competenceType === "single" && !singleMonth) {
        snackbar("Selecione o mês", "error");
        return;
      }
      if (competenceType === "range") {
        if (!rangeStart || !rangeEnd) {
          snackbar("Preencha o período completo", "error");
          return;
        }
        const start = parseCompetence(rangeStart);
        const end = parseCompetence(rangeEnd);
        const startDate = dayjs(`${start.year}-${start.month}-01`);
        const endDate = dayjs(`${end.year}-${end.month}-01`);

        if (startDate.isSame(endDate, "month")) {
          snackbar(
            "No modo período, é necessário selecionar pelo menos 2 meses diferentes. Use a opção 'Mensal' para um único mês.",
            "error",
          );
          return;
        }
      }
    } else if (activeStep === 1) {
      if (!hasErp) {
        snackbar("Selecione se possui ERP integrado", "error");
        return;
      }
      if (hasErp === "yes" && !selectedErp) {
        snackbar("Selecione o ERP", "error");
        return;
      }
      if (hasErp === "yes" && selectedErp === "Outros" && !otherErp) {
        snackbar("Informe o nome do ERP", "error");
        return;
      }
      if (hasErp === "no") {
        setErpNoDialogOpen(true);
        return;
      }
    } else if (activeStep === 2) {
      if (draftId) {
        const hasFiles = documentsFiles.length > 0;
        const hasComment = replyComment.trim().length > 0;
        if (!hasFiles && !hasComment) {
          snackbar(
            "Envie pelo menos um anexo ou preencha a mensagem de resposta.",
            "error",
          );
          return;
        }
        setErrors({});
      } else {
        const validation = validateAllMonths();
        if (!validation.allMonthsHaveExtract) {
          if (competenceType === "single") {
            setErrors({ ...errors, extract: "Envio de extrato é obrigatório" });
          } else {
            setErrors({
              ...errors,
              extract: `É necessário anexar extrato para todos os meses do período. Faltam: ${validation.missingMonths.join(
                ", ",
              )}`,
            });
          }
          snackbar("Anexe os arquivos obrigatórios antes de avançar", "error");
          return;
        }

        if (
          hasErp === "no" &&
          validation.allMonthsHaveFinancialReport !== undefined &&
          !validation.allMonthsHaveFinancialReport
        ) {
          if (competenceType === "single") {
            setErrors({
              ...errors,
              financialReport:
                "Envio de relatório financeiro é obrigatório quando não possui ERP integrado",
            });
          } else {
            setErrors({
              ...errors,
              financialReport: `É necessário anexar relatório financeiro para todos os meses do período quando não possui ERP integrado. Faltam: ${validation.missingFinancialReportMonths?.join(
                ", ",
              )}`,
            });
          }
          snackbar("Anexe os arquivos obrigatórios antes de avançar", "error");
          return;
        }

        setErrors({});
      }
    }

    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleErpNoDialogClose = () => {
    setErpNoDialogOpen(false);
    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (guardUploading()) return;
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <CompetenceSelector
            competenceType={competenceType}
            singleMonth={singleMonth}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            occupiedPeriods={occupiedPeriods}
            onCompetenceTypeChange={(type) => setCompetenceType(type)}
            onSingleMonthChange={setSingleMonth}
            onRangeStartChange={setRangeStart}
            onRangeEndChange={setRangeEnd}
          />
        );
      case 1:
        return (
          <ERPSelector
            hasErp={hasErp}
            selectedErp={selectedErp}
            otherErp={otherErp}
            error={errors.erp}
            onHasErpChange={(value) => {
              setHasErp(value);
              setSelectedErp("");
              setOtherErp("");
              setErrors({ ...errors, erp: undefined });
            }}
            onSelectedErpChange={(value) => {
              setSelectedErp(value);
              setOtherErp("");
              setErrors({ ...errors, erp: undefined });
            }}
            onOtherErpChange={(value) => {
              setOtherErp(value);
              setErrors({ ...errors, erp: undefined });
            }}
            onErrorClear={() => setErrors({ ...errors, erp: undefined })}
          />
        );
      case 2:
        if (draftId) {
          const maxReplyChars = 500;
          const pendingBlockMinHeight = 200;
          return (
            <Stack spacing={3}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems="stretch"
              >
                <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Anexos
                  </Typography>
                  <Box sx={{ minHeight: pendingBlockMinHeight }}>
                    <FileUploadTable
                      title=""
                      allowedFiles=".pdf,.txt,.jpg,.jpeg,.png,.xls,.xlsx,.ofx,.csv"
                      disabled={isSubmitting}
                      files={getFilesForCurrentMonth(documentsFiles, "docs")}
                      onFilesSelected={(files) =>
                        handleFilesSelected(files, "docs", true)
                      }
                      onDelete={(id) => handleRemoveStagedFile(id, "docs")}
                    />
                  </Box>
                </Stack>
                <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="body2" fontWeight="bold">
                      Mensagem
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {replyComment.length}/{maxReplyChars}
                    </Typography>
                  </Stack>
                  <TextField
                    multiline
                    minRows={2}
                    maxRows={12}
                    value={replyComment}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setReplyComment(e.target.value.slice(0, maxReplyChars))
                    }
                    placeholder="Caso necessário, digite uma mensagem para nossa equipe operacional."
                    fullWidth
                    inputProps={{ maxLength: maxReplyChars }}
                    sx={{
                      "& .MuiInputBase-root": { alignItems: "flex-start" },
                      "& .MuiInputBase-input": {
                        overflowY: "auto",
                        minHeight: 82,
                        maxHeight: 200,
                      },
                    }}
                  />
                </Stack>
              </Stack>
            </Stack>
          );
        }
        return (
          <Stack spacing={3}>
            {competenceType === "range" && (
              <MonthTabs
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                selectedTab={selectedMonthTab}
                onTabChange={setSelectedMonthTab}
              />
            )}

            <Grid container spacing={2}>
              <Grid xs={4}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" fontWeight="bold">
                    Envio de extrato *
                  </Typography>
                  <Tooltip
                    title={
                      <Stack spacing={0.5}>
                        <Typography variant="caption">
                          É sempre obrigatório enviar um extrato mensal em
                          alguma das extensões permitidas.
                        </Typography>
                        <Typography variant="caption">
                          Extensões permitidas: XLSX, XLS, OFX, CSV e PDF
                        </Typography>
                      </Stack>
                    }
                  >
                    <IconButton size="small" sx={{ p: 0.5 }}>
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <FileUploadTable
                  key={getDragAndDropKey("extrato")}
                  title=""
                  allowedFiles=".xlsx,.xls,.ofx,.csv,.pdf"
                  disabled={isSubmitting}
                  files={getFilesForCurrentMonth(extractFiles, "extrato")}
                  onFilesSelected={(files) =>
                    handleFilesSelected(files, "extrato")
                  }
                  onDelete={(id) => handleRemoveStagedFile(id, "extrato")}
                />
                {errors.extract && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {errors.extract}
                  </Alert>
                )}
              </Grid>

              <Grid xs={4}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" fontWeight="bold">
                    Envio de relatório financeiro{hasErp === "no" ? " *" : ""}
                  </Typography>
                  <Tooltip
                    title={
                      <Stack spacing={0.5}>
                        <Typography variant="caption">
                          Obrigatório apenas quando não possui ERP integrado.
                        </Typography>
                        <Typography variant="caption">
                          Extensões permitidas: XLSX, CSV e TXT
                        </Typography>
                      </Stack>
                    }
                  >
                    <IconButton size="small" sx={{ p: 0.5 }}>
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <FileUploadTable
                  key={getDragAndDropKey("relatorioFinanc")}
                  title=""
                  allowedFiles=".xlsx,.csv,.txt"
                  disabled={isSubmitting}
                  files={getFilesForCurrentMonth(
                    financialReportFiles,
                    "relatorioFinanc",
                  )}
                  onFilesSelected={(files) =>
                    handleFilesSelected(files, "relatorioFinanc")
                  }
                  onDelete={(id) =>
                    handleRemoveStagedFile(id, "relatorioFinanc")
                  }
                />
                {errors.financialReport && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {errors.financialReport}
                  </Alert>
                )}
              </Grid>

              <Grid xs={4}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" fontWeight="bold">
                    Envio de documentos
                  </Typography>
                  <Tooltip
                    title={
                      <Stack spacing={0.5}>
                        <Typography variant="caption">
                          Envio opcional para documentos extras, como notas
                          fiscais, boletos, comprovantes, etc., solicitados pelo
                          time contábil.
                        </Typography>
                        <Typography variant="caption">
                          Extensões permitidas: PDF, TXT, JPG e PNG
                        </Typography>
                      </Stack>
                    }
                  >
                    <IconButton size="small" sx={{ p: 0.5 }}>
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <FileUploadTable
                  key={getDragAndDropKey("docs")}
                  title=""
                  allowedFiles=".pdf,.txt,.jpg,.jpeg,.png"
                  disabled={isSubmitting}
                  files={getFilesForCurrentMonth(documentsFiles, "docs")}
                  onFilesSelected={(files) =>
                    handleFilesSelected(files, "docs")
                  }
                  onDelete={(id) => handleRemoveStagedFile(id, "docs")}
                />
              </Grid>
            </Grid>
          </Stack>
        );
      case 3: {
        const formatPeriod = () => {
          if (competenceType === "single") {
            return singleMonth;
          } else {
            return `${rangeStart} a ${rangeEnd}`;
          }
        };

        const getErpInfo = () => {
          if (hasErp === "yes") {
            return selectedErp === "Outros" ? otherErp : selectedErp;
          }
          return "Não possui ERP integrado";
        };

        return (
          <Stack spacing={3}>
            <Typography variant="h6">Resumo do Período Contábil</Typography>

            <Card variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Stack>
                  <Typography variant="body2" color="text.secondary">
                    Tipo de Período
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {competenceType === "single" ? "Mensal" : "Período"}
                  </Typography>
                </Stack>

                <Divider />

                <Stack>
                  <Typography variant="body2" color="text.secondary">
                    Período Selecionado
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatPeriod()}
                  </Typography>
                </Stack>

                <Divider />

                <Stack>
                  <Typography variant="body2" color="text.secondary">
                    ERP Integrado
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {getErpInfo()}
                  </Typography>
                </Stack>

                <Divider />

                <Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Arquivos Anexados
                  </Typography>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">
                        Extratos: {allFilesCount.extrato} arquivo(s)
                      </Typography>
                      {allFilesCount.extrato === 0 && (
                        <Typography variant="body2" color="error">
                          Obrigatório
                        </Typography>
                      )}
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">
                        Relatórios Financeiros: {allFilesCount.relatorioFinanc}{" "}
                        arquivo(s)
                      </Typography>
                      {hasErp === "no" &&
                        allFilesCount.relatorioFinanc === 0 && (
                          <Typography variant="body2" color="error">
                            Obrigatório
                          </Typography>
                        )}
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">
                        Documentos: {allFilesCount.docs} arquivo(s)
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </Card>

            <Typography variant="body2" color="text.secondary">
              Revise as informações acima antes de enviar. Após o envio, o
              período entrará em fila de processamento pelo nosso time.
            </Typography>
          </Stack>
        );
      }
      default:
        return null;
    }
  };

  if (pendingLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Grid container spacing={3}>
        <Grid xs={12}>
          <Card>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ m: 2 }}
            >
              <Button
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={handleSair}
                disabled={isSubmitting}
              >
                Sair
              </Button>
              {draftId && (
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  onClick={() => setShowPendenciasModal(true)}
                >
                  Pendências
                </Button>
              )}
            </Stack>
            <Stack spacing={3} sx={{ p: 3 }}>
              <Stepper activeStep={activeStep}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Divider />

              {renderStepContent()}

              <Stack
                direction="row"
                spacing={2}
                justifyContent="space-between"
                sx={{ pt: 2 }}
              >
                {!draftId && (
                  <Button
                    disabled={activeStep === 0 || isSubmitting}
                    onClick={handleBack}
                    startIcon={<ArrowBackIcon />}
                  >
                    Voltar
                  </Button>
                )}
                {draftId && <Box />}
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    startIcon={<SendIcon />}
                    onClick={handleSendClick}
                    disabled={isSubmitting}
                  >
                    Enviar
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<ArrowForwardIcon />}
                    disabled={isSubmitting}
                  >
                    Avançar
                  </Button>
                )}
              </Stack>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      <MuiModal
        open={erpNoDialogOpen}
        onClose={handleErpNoDialogClose}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          },
        }}
      >
        <Card
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "90%", md: 500 },
            maxWidth: 500,
            maxHeight: { xs: "90vh", md: "85vh" },
            p: 0,
            zIndex: 1301,
            overflow: "auto",
          }}
        >
          <CardHeader
            sx={{ borderBottom: "1px solid #CCCC" }}
            title={
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h6" component="span" fontWeight={600}>
                  Importância do ERP
                </Typography>
                <IconButton
                  aria-label="fechar"
                  size="small"
                  onClick={handleErpNoDialogClose}
                  edge="end"
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            }
          />
          <CardContent sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="body2">
                Um sistema ERP (Enterprise Resource Planning) é fundamental para
                a gestão e organização financeira e contábil da sua organização.
                Ele permite:
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2">
                  • Integração de processos financeiros e contábeis
                </Typography>
                <Typography variant="body2">
                  • Maior precisão e controle dos dados
                </Typography>
                <Typography variant="body2">
                  • Automação de processos e redução de erros
                </Typography>
                <Typography variant="body2">
                  • Melhor rastreabilidade e auditoria
                </Typography>
                <Typography variant="body2">
                  • Economia de tempo e recursos
                </Typography>
              </Stack>
              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  Sem um ERP integrado, será obrigatório o envio de relatórios
                  financeiros manualmente para cada período.
                </Typography>
              </Alert>
              <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
                <Button
                  onClick={handleErpNoDialogClose}
                  variant="contained"
                  sx={{ textTransform: "none" }}
                >
                  Entendi
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </MuiModal>

      <MuiModal
        open={showPendenciasModal}
        onClose={() => setShowPendenciasModal(false)}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: { backgroundColor: "rgba(0, 0, 0, 0.7)" },
        }}
      >
        <Card
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "90%", md: 500 },
            maxWidth: 500,
            maxHeight: { xs: "90vh", md: "85vh" },
            p: 0,
            zIndex: 1301,
            overflow: "auto",
          }}
        >
          <CardHeader
            sx={{ borderBottom: "1px solid #CCCC" }}
            title={
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="h6" component="span" fontWeight={600}>
                    Pendências
                  </Typography>
                  <IconButton
                    aria-label="fechar"
                    size="small"
                    onClick={() => setShowPendenciasModal(false)}
                    edge="end"
                  >
                    <CloseIcon />
                  </IconButton>
                </Stack>
                <Typography variant="body2" component="span">
                  Confira abaixo as pendências identificadas pela nossa equipe
                </Typography>
              </>
            }
          />
          <CardContent sx={{ p: 2 }}>
            <Box
              sx={{
                maxHeight: "60vh",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <Typography
                variant="body2"
                component="div"
                sx={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {taskComment || "Nenhum comentário de pendência registrado."}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </MuiModal>

      <Dialog
        open={confirmSendOpen}
        onClose={() => {
          if (isSubmitting) return;
          setConfirmSendOpen(false);
        }}
      >
        <DialogTitle>Confirmar envio</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deseja realmente enviar os documentos para realização do processo
            contábil?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmSendOpen(false)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmSend}
            variant="contained"
            color="warning"
            disabled={isSubmitting}
          >
            Sim, enviar
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        open={isSubmitting}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 10,
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="body1">
          {submitProgressLabel || "Enviando período contábil..."}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "rgba(255, 193, 7, 0.12)",
            border: "1px solid rgba(255, 193, 7, 0.35)",
            borderRadius: 1,
            px: 2,
            py: 1,
            maxWidth: 340,
          }}
        >
          <WarningAmberIcon
            sx={{ fontSize: 18, color: "#ffc107", flexShrink: 0 }}
          />
          <Typography
            variant="caption"
            sx={{ color: "#ffc107", lineHeight: 1.5 }}
          >
            Não feche nem recarregue esta página — o envio em andamento será
            perdido.
          </Typography>
        </Box>
      </Backdrop>

      <Dialog
        open={failedFilesDialog.open}
        onClose={() => resolveFailedFilesDialog("cancel")}
      >
        <DialogTitle>Falha no envio de arquivos</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Não foi possível enviar o(s) arquivo(s) abaixo. Você pode tentar
            novamente ou continuar sem eles — nesse caso, envie-os
            posteriormente por e-mail ao nosso suporte.
          </DialogContentText>
          <List dense>
            {failedFilesDialog.files.map((f) => (
              <ListItem key={f.id}>
                <ListItemText primary={f.name} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => resolveFailedFilesDialog("cancel")}>
            Cancelar
          </Button>
          <Button onClick={() => resolveFailedFilesDialog("skip")}>
            Continuar sem estes arquivos
          </Button>
          <Button
            onClick={() => resolveFailedFilesDialog("retry")}
            variant="contained"
          >
            Tentar novamente
          </Button>
        </DialogActions>
      </Dialog>

      <MuiModal
        open={showFeedbackModal}
        onClose={() => {
          if (feedbackSubmitting) return;
          setShowFeedbackModal(false);
          navigate("/contabil");
        }}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: { backgroundColor: "rgba(0, 0, 0, 0.7)" },
        }}
      >
        <Card
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "90%", md: 700 },
            maxWidth: 700,
            maxHeight: { xs: "90vh", md: "85vh" },
            p: 0,
            zIndex: 1301,
            overflow: "auto",
          }}
        >
          <CardHeader
            sx={{ borderBottom: "1px solid #CCCC" }}
            title={
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h6" component="span" fontWeight={600}>
                  Avaliação de funcionalidade
                </Typography>
                <IconButton
                  aria-label="fechar"
                  size="small"
                  onClick={() => {
                    if (feedbackSubmitting) return;
                    setShowFeedbackModal(false);
                    navigate("/contabil");
                  }}
                  edge="end"
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            }
          />
          <CardContent sx={{ p: 2 }}>
            <FeatureFeedback
              submitting={feedbackSubmitting}
              onSubmit={async ({ rating, feedback }) => {
                setFeedbackSubmitting(true);
                try {
                  await fetchData("/feedback/ces", {
                    method: "POST",
                    body: JSON.stringify({
                      context: "ces_accounting_draft",
                      rating,
                      comments: feedback || null,
                      clientId: company._id,
                      clientReference: company.reference,
                    }),
                  });
                } catch (e) {
                  console.log(e);
                }
                setFeedbackSubmitting(false);
              }}
              onSkip={async () => {
                setFeedbackSubmitting(true);
                try {
                  await fetchData("/feedback/skip", {
                    method: "POST",
                    body: JSON.stringify({ context: "ces_accounting_draft" }),
                  });
                } catch (e) {
                  console.log(e);
                }
                setFeedbackSubmitting(false);
                setShowFeedbackModal(false);
                navigate("/contabil");
              }}
              onClose={() => {
                setShowFeedbackModal(false);
                navigate("/contabil");
              }}
            />
          </CardContent>
        </Card>
      </MuiModal>
    </>
  );
};

export default NewPeriod;
